import { useMemo } from 'react';
import { I18n } from '@aws-amplify/core';
import { useGetResolution886ReviewResults } from '@/hooks/v2/resolution-886';
import { useResolution886Report } from '@/context/Resolution886Report';
import { getReviewId } from '../../../_hooks/getReviewId';

type RiskFactorRow = {
	key: string;
	categoryName: string;
	totalExposureTime: string;
	[activityKey: string]: string;
};

export const useGetRiskFactors = (report: any) => {
	const { selectedReviewId } = useResolution886Report();

	const reviewId = getReviewId(selectedReviewId, report?.reviews);

	const {
		data: reviewResults,
		isLoading,
		error
	} = useGetResolution886ReviewResults({
		report_id: report?.id,
		company_id: report?.company?.id,
		organization_id: report?.organization?.id,
		review_id: reviewId
	});

	const { riskFactors, taskKeys, taskNames } = useMemo(() => {
		if (!reviewResults?.assessments) {
			return { riskFactors: [], taskKeys: [], taskNames: [] };
		}

		const assessments = reviewResults.assessments;

		const allCategoryResults = assessments.flatMap((assessment) => assessment.category_results);
		const uniqueCategories = allCategoryResults.reduce((acc: any[], current) => {
			const existingIndex = acc.findIndex((item) => item.category.id === current.category.id);
			if (existingIndex === -1) {
				acc.push(current);
			}
			return acc;
		}, []);

		const taskKeys = assessments.map((_, index) => `task_${index + 1}`);
		const taskNames = assessments.map((assessment) => assessment.activity?.name || '');

		const riskFactors: RiskFactorRow[] = uniqueCategories.map((categoryResult) => {
			const totalExposureTime = assessments.reduce((sum, assessment) => {
				const taskResult = assessment.category_results.find(
					(cr) => cr.category.id === categoryResult.category.id
				);
				const exposureTime = taskResult?.exposure_time ? Number(taskResult.exposure_time) : 0;
				return sum + exposureTime;
			}, 0);

			const rowData: RiskFactorRow = {
				key: categoryResult.category.id,
				categoryName: I18n.get(categoryResult.category?.name || '-'),
				totalExposureTime: totalExposureTime > 0 ? `${totalExposureTime}%` : '0%'
			};

			assessments.forEach((assessment, index) => {
				const taskResult = assessment.category_results.find(
					(cr) => cr.category.id === categoryResult.category.id
				);

				rowData[`task_${index + 1}`] = taskResult?.risk || 'TOLERABLE';
			});

			return rowData;
		});

		return { riskFactors, taskKeys, taskNames };
	}, [reviewResults, selectedReviewId]);

	return { riskFactors, taskKeys, taskNames, isLoading, error };
};
